// SPDX-FileCopyrightText: 2017-2022 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import type FiniteDateRange from 'lib-common/finite-date-range'
import type { Translations } from 'lib-components/i18n'

const components: Translations = {
  asyncButton: {
    inProgress: 'Loading',
    failure: 'Failed to load',
    success: 'Success'
  },
  combobox: {
    openDropdown: 'Open menu',
    closeDropdown: 'Close menu'
  },
  common: {
    add: 'Add',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    no: 'No',
    noResults: 'No results',
    noFirstName: 'No first name',
    noLastName: 'No last name',
    open: 'Open',
    remove: 'Remove',
    saving: 'Saving',
    saved: 'Saved',
    search: 'Search',
    yes: 'Yes',
    openExpandingInfo: 'Open the details',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    selected: 'Selected',
    unselected: 'Unselected',
    userTypes: {
      SYSTEM: 'system',
      CITIZEN: 'citizen',
      EMPLOYEE: 'employee',
      MOBILE_DEVICE: 'mobile device',
      UNKNOWN: 'unknown'
    }
  },
  datePicker: {
    placeholder: 'dd.mm.yyyy',
    description: 'Type the date in dd.mm.yyyy format.',
    validationErrors: {
      validDate: 'Valid date format is dd.mm.yyyy',
      dateTooEarly: 'Pick a later date',
      dateTooLate: 'Pick an earlier date'
    },
    open: 'Open date picker',
    close: 'Close date picker'
  },
  documentTemplates: {
    // only on employee frontend
    templateQuestions: {
      label: 'Label',
      options: 'Options',
      infoText: 'Info text',
      multiline: 'Allow multiline answer',
      withText: 'With extra info',
      text: 'Text content',
      textFields: 'Text fields',
      addTextField: 'Add a text field',
      allowMultipleRows: 'Allow multiple answers',
      addRow: 'Add new row'
    },
    noSelection: 'No selection',
    documentStates: {
      DRAFT: 'Draft',
      PREPARED: 'Ready',
      CITIZEN_DRAFT: 'To be filled by guardian',
      DECISION_PROPOSAL: 'Decision proposal',
      COMPLETED: 'Completed',
      ACCEPTED: 'Accepted',
      REJECTED: 'Rejected',
      ANNULLED: 'Annulled'
    }
  },
  fileUpload: {
    loading: 'Loading',
    loaded: 'Loaded',
    error: {
      EXTENSION_MISSING: 'Missing file extension',
      EXTENSION_INVALID: 'Invalid file extension',
      INVALID_CONTENT_TYPE: 'Invalid content type',
      FILE_TOO_LARGE: (maxMB) => `File is too big (max. ${maxMB} MB)`,
      SERVER_ERROR: 'Upload failed'
    },
    input: {
      title: 'Add an attachment',
      text: (fileTypes, maxMB) =>
        `Press here or drag and drop a new file. Max file size: ${maxMB} MB. Allowed formats: ` +
        fileTypes
          .map((type) => {
            switch (type) {
              case 'image':
                return 'images (JPEG, JPG, PNG)'
              case 'document':
                return 'documents (PDF, DOC, DOCX, ODT)'
              case 'audio':
                return 'audio'
              case 'video':
                return 'video'
              case 'csv':
                return 'CSV files'
            }
          })
          .join(', ')
    },
    uploadFile: 'Upload file',
    deleteFile: 'Delete file'
  },
  loginErrorModal: {
    header: 'Login failed',
    message:
      'The identification process failed or was stopped. To log in, go back and try again.',
    returnMessage: 'Go back'
  },
  messages: {
    staffAnnotation: 'Staff',
    recipients: 'Recipients',
    message: 'Message',
    send: 'Send',
    sending: 'Sending',
    types: {
      MESSAGE: 'Message',
      BULLETIN: 'Bulletin'
    },
    thread: {
      type: 'Type',
      urgent: 'Urgent'
    },
    outOfOffice: {
      singleRecipient: (name: string, period: FiniteDateRange) =>
        `${name} is out of office during ${period.format()}.`,
      multipleRecipientsHeader:
        'These recipients are out of office during the following periods.'
    }
  },
  messageReplyEditor: {
    // Currently only used for Finnish frontends
    discard: '',
    messagePlaceholder: undefined,
    messagePlaceholderSensitiveThread: undefined
  },
  sessionTimeout: {
    sessionExpiredTitle: 'Your session has timed out',
    sessionExpiredMessage: 'Please log in again.',
    goToLoginPage: 'Go to login page',
    cancel: 'Cancel'
  },
  notifications: {
    close: 'Close'
  },
  offlineNotification: 'No internet connection',
  reloadNotification: {
    title: 'New version of eVaka is available',
    buttonText: 'Reload page'
  },
  treeDropdown: {
    // Currently only used for Finnish frontends
    expand: () => '',
    collapse: () => '',
    expandDropdown: ''
  },
  validationErrors: {
    required: 'Value missing',
    requiredSelection: 'Please select one of the options',
    format: 'Give value in correct format',
    integerFormat: 'Give an integer value',
    ssn: 'Invalid person identification number',
    phone: 'Invalid telephone number',
    email: 'Invalid email',
    preferredStartDate: 'Invalid preferred start date',
    connectedPreferredStartDate:
      "You can apply for supplementary early childhood education from the start of your child's preschool education.",
    timeFormat: 'Check',
    timeRequired: 'Required',
    exceedsMaxDuration: 'Exceeds maximum early childhood education time',
    dateTooEarly: 'Pick a later date',
    dateTooLate: 'Pick an earlier date',
    unitNotSelected: 'Pick at least one choice',
    emailsDoNotMatch: 'The emails do not match',
    httpUrl: 'Valid url format is https://example.com',
    unselectableDate: 'Invalid date',
    openAttendance: 'Open attendance',
    generic: 'Check'
  },
  metadata: {
    title: 'Archivable metadata',
    notFound: 'No archivable metadata for the document',
    case: 'Case',
    caseIdentifier: 'Case identifier',
    processName: 'Case process',
    organization: 'Organization',
    organizationName:
      'City of Espoo pre-primary education and early childhood education',
    archiveDurationMonths: 'Archiving period',
    primaryDocument: 'Primary document',
    secondaryDocuments: 'Other documents',
    documentId: 'Document identifier',
    createdAt: 'Creation date',
    createdBy: 'Creator',
    publishedVersions: 'Published versions',
    notPublished: 'Not published',
    monthsUnit: 'months',
    confidentiality: 'Confidentiality',
    confidential: 'Confidential',
    public: 'Public',
    notSet: 'Not set',
    confidentialityDuration: 'Confidentiality period',
    confidentialityBasis: 'Confidentiality basis',
    years: 'years',
    receivedBy: {
      label: 'Received',
      PAPER: 'On paper',
      ELECTRONIC: 'Electronically'
    },
    sfiDelivery: {
      label: 'Suomi.fi deliveries',
      readAt: 'opened',
      method: {
        ELECTRONIC: 'Electronically',
        PAPER_MAIL: 'Paper mail',
        PENDING: 'Waiting for delivery'
      }
    },
    history: 'Process history',
    downloadPdf: 'Download PDF',
    states: {
      INITIAL: 'Initiation of the case',
      PREPARATION: 'Preparation of the case',
      DECIDING: 'Decision making',
      COMPLETED: 'Implementation / Completion / Closing'
    },
    section: {
      show: 'Show more information',
      hide: 'Hide more information'
    },
    applicationDocumentNames: {
      DAYCARE: 'Application for early childhood education and service voucher',
      PRESCHOOL:
        'Enrolment to pre-primary education and/or preparatory education',
      CLUB: 'Application for club'
    },
    decisionDocumentNames: {
      CLUB: 'Decision on club',
      DAYCARE: 'Decision on early childhood education',
      DAYCARE_PART_TIME: 'Decision on part-day early childhood education',
      PRESCHOOL: 'Decision on pre-primary education',
      PRESCHOOL_DAYCARE:
        'Decision on early childhood education related to pre-primary education',
      PRESCHOOL_CLUB: 'Decision on pre-primary education club',
      PREPARATORY_EDUCATION: 'Decision on preparatory education'
    },
    financeDecisionDocumentNames: {
      FEE_DECISION: 'Fee decision',
      VOUCHER_VALUE_DECISION: 'Voucher value decision'
    },
    processNames: {
      APPLICATION_DAYCARE:
        'Application for early childhood education / service voucher application for early childhood education',
      APPLICATION_PRESCHOOL:
        'Application for pre-primary education / application for preparatory education for basic education provided in connection with pre-primary education',
      APPLICATION_CLUB: 'Application for club',
      FEE_DECISION: 'Early childhood education fee decision',
      VOUCHER_VALUE_DECISION:
        'Early childhood education service voucher value decision',
      CHILD_DOCUMENT_VASU: "Child's early childhood education plan",
      CHILD_DOCUMENT_LEOPS: "Child's pre-primary education learning plan",
      CHILD_DOCUMENT_HOJKS: 'Individual education plan',
      CHILD_DOCUMENT_PEDAGOGICAL_ASSESSMENT:
        'Pre-primary education pedagogical assessment',
      CHILD_DOCUMENT_PEDAGOGICAL_REPORT:
        'Pre-primary education pedagogical report'
    }
  }
}

export default components
